
import React, { useState, useEffect } from 'react';
import { useCollegeSearch, College } from '@/hooks/useCollegeSearch';
import CollegeSearchDisplay from './CollegeSearchDisplay';
import CollegeModal from './CollegeModal';
import SuggestCorrectionModal from './SuggestCorrectionModal';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const SuggestedCollegesTab: React.FC = () => {
  const {
    suggestedColleges,
    loading,
    error,
    toggleSaveCollege,
    fetchSuggestedColleges
  } = useCollegeSearch();

  const [selectedCollege, setSelectedCollege] = useState<College | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Add state for college correction modal
  const [suggestedCollege, setSuggestedCollege] = useState<College | null>(null);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);

  useEffect(() => {
    // Fetch suggested colleges on component mount
    fetchSuggestedColleges();
  }, []);

  const handleCollegeClick = (college: College) => {
    setSelectedCollege(college);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedCollege(null);
  };

  const handleSuggestCorrection = (collegeId: string) => {
    const college = suggestedColleges.find(c => c.id === collegeId);
    if (college) {
      setSuggestedCollege(college);
      setShowCorrectionModal(true);
    }
  };
  
  const handleCloseCorrectionModal = () => {
    setShowCorrectionModal(false);
    setSuggestedCollege(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-medium mb-4">Suggested Colleges For You</h2>
      
      {loading && !suggestedColleges.length ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
          <p className="ml-2">Loading suggested colleges...</p>
        </div>
      ) : suggestedColleges.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-lg text-gray-600 mb-4">
            No suggested colleges found. Save some colleges to get better recommendations.
          </p>
          <Button onClick={() => fetchSuggestedColleges()}>Refresh Suggestions</Button>
        </div>
      ) : (
        <>
          <p className="text-gray-600 mb-4">
            Based on your profile and previous searches, these colleges might be a good match for you.
          </p>
          
          <CollegeSearchDisplay
            colleges={suggestedColleges}
            loading={loading}
            loadingMore={false}
            hasMore={false}
            onLoadMore={() => {}}
            onToggleSave={toggleSaveCollege}
            showSavedDetails={false}
            onCollegeClick={handleCollegeClick}
            onSuggestCorrection={handleSuggestCorrection}
          />
        </>
      )}
      
      {selectedCollege && (
        <CollegeModal
          college={selectedCollege}
          open={modalOpen}
          onClose={handleCloseModal}
          onToggleSave={toggleSaveCollege}
          onSuggestCorrection={handleSuggestCorrection}
        />
      )}
      
      {/* Add the suggestion correction modal */}
      <SuggestCorrectionModal
        college={suggestedCollege}
        open={showCorrectionModal}
        onClose={handleCloseCorrectionModal}
      />
    </div>
  );
};

export default SuggestedCollegesTab;
